import React, { useState, useEffect, useRef } from "react";
import { baseOptions } from "../../utils/helpers";
import starsIcon from "../../assets/icons/stars.svg";
import upperArrowIcon from "../../assets/images/up.png";

interface ChatInputProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSendMessage: () => void;
  showFeedback: boolean;
  isVisible: boolean;
  isMaximized?: boolean;
  onOptionClick?: (optionText: string) => void;
  hasAIResponded?: boolean;
  textSize?: number;
  propertyData?: any;
  isInstructionalPanelOpen?: boolean;
  hasProvidedAddress?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  setInputValue,
  onSendMessage,
  showFeedback,
  isVisible,
  isMaximized = false,
  onOptionClick,
  hasAIResponded = false,
  textSize = 100,
  propertyData,
  isInstructionalPanelOpen = false,
  hasProvidedAddress = false,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [hasSentMessage, setHasSentMessage] = useState(false);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const [shouldShowEllipsis, setShouldShowEllipsis] = useState(false);

  const checkOptionTruncation = () => {
    if (optionsContainerRef.current && isInstructionalPanelOpen) {
      const buttons = optionsContainerRef.current.querySelectorAll(".chat-option");
      if (buttons.length >= 3) {
        const thirdButton = buttons[2] as HTMLElement;
        const isOverflowing = thirdButton.scrollWidth > thirdButton.clientWidth;
        setShouldShowEllipsis(isOverflowing);
      }
    } else {
      setShouldShowEllipsis(false);
    }
  };

  useEffect(() => {
    checkOptionTruncation();
    const handleResize = () => checkOptionTruncation();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showOptions, textSize, isInstructionalPanelOpen]);

  const toggleMoreOptions = () => {
    setShowMoreOptions(!showMoreOptions);
  };

  const handleSendMessage = () => {
    setHasSentMessage(true);
    onSendMessage();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const handleOptionClick = (optionText: string) => {
    if (onOptionClick) {
      onOptionClick(optionText);
    } else {
      setInputValue(optionText);
      setTimeout(() => handleSendMessage(), 100);
    }
    setShowMoreOptions(false);
  };

  // Effect to handle auto-showing options after 20 seconds of inactivity
  useEffect(() => {
    let inactivityTimer: NodeJS.Timeout;

    if (isVisible && isMaximized && !hasTyped) {
      inactivityTimer = setTimeout(() => {
        setShowOptions(true);
      }, 20000);
    }

    return () => {
      clearTimeout(inactivityTimer);
    };
  }, [isVisible, isMaximized, hasTyped]);

  // Effect to show options after AI has responded to user's first message
  useEffect(() => {
    if (hasSentMessage && hasAIResponded && isMaximized) {
      setShowOptions(true);
    }
  }, [hasSentMessage, hasAIResponded, isMaximized]);

  // Effect to focus input when visible
  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  // Get all available options including Change address if user has provided address
  const getAllOptions = () => {
    const options = [...baseOptions];
    if (hasProvidedAddress) {
      options.push({ text: "Change address", option: "change_address" });
    }
    return options;
  };

  const allOptions = getAllOptions();
  const [firstThreeOptions, setFirstThreeOptions] = useState(allOptions.slice(0, 3));
  const [remainingOptions, setRemainingOptions] = useState(allOptions.slice(3));

  // No truncation - show full text for all options
  const getTruncatedText = (text: string, isLastOption: boolean) => {
    // Return the full text without any truncation
    return text;
  };

  function getTextWidths(texts: string[]): number[] {
    const font = "12px Arial";
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return new Array(texts.length).fill(100);
    ctx.font = font;
    return texts.map((text) => {
      const textWidth = ctx.measureText(text).width;
      return Math.ceil(textWidth);
    });
  }

  function getOptionWidth() {
    if (!optionsContainerRef.current || !inputRef.current) {
      // console.log("Container ref or input ref not available");
      return;
    }
    
    // Use the input field width as the basis for our calculations
    // This ensures options adapt to the actual input field width
    const inputWidth = inputRef.current.clientWidth;
    
    // Subtract some padding to ensure options don't overflow the input field
    // This is a direct approach to make sure options stay within the input field width
    const availableWidth = inputWidth - 40; // 40px buffer for safety
    
    // console.log(`Input width: ${inputWidth}px, Available width: ${availableWidth}px, Panel open: ${isInstructionalPanelOpen}`);
    
    if (availableWidth <= 0) {
      // console.log("Available width is too small, using fallback");
      setFirstThreeOptions(allOptions.slice(0, 2));
      setRemainingOptions(allOptions.slice(2));
      return;
    }
    
    // Compute widths using real text measurements with font size adjustment
    const textWidths = getTextWidths(allOptions.map(opt => opt.text));
    
    // Adjust measurements for the increased font size (15px instead of 12px)
    const fontSizeRatio = 15 / 12; // Current font size / original font size
    
    const optionWidths = allOptions.map((option, index) => {
      const hasIcon = option.text !== "Change address";
      
      // Use real measured text width + icon + padding with font size adjustment
      const textWidth = textWidths[index] * fontSizeRatio;
      const iconWidth = hasIcon ? 21 : 0; // 16px icon + 5px margin
      const padding = 16; // From memory: reduced padding from 20px to 16px
      const margin = 3;  // From memory: reduced margin from 5px to 3px
      
      return textWidth + iconWidth + padding + margin;
    });
    
    // console.log("Option widths:", optionWidths);
    
    // Track selected options
    const first: typeof allOptions = [];
    const remaining: typeof allOptions = [];
    let usedWidth = 0;
    
    // Use smaller button width to allow more space for options
    const moreButtonWidth = 45;
    
    // Dynamically determine how many options to show based on available width
    // Calculate how many options we can fit in the available width
    let maxOptionsToFit = 0;
    let tempWidth = 0;
    
    // More aggressive approach to fit as many options as possible
    // First calculate total width of all options
    const totalOptionWidth = optionWidths.reduce((sum, width) => sum + width, 0);
    
    // If all options fit, show them all
    if (totalOptionWidth <= availableWidth) {
      maxOptionsToFit = allOptions.length;
    } else {
      // Otherwise, calculate how many options we can fit with the "+N" button
      let cumulativeWidth = 0;
      
      for (let i = 0; i < allOptions.length; i++) {
        // Reserve space for "+N" button if there are more options after this one
        const needsMoreButton = i < allOptions.length - 1;
        const effectiveWidth = availableWidth - (needsMoreButton ? moreButtonWidth : 0);
        
        // Add this option's width to our running total
        cumulativeWidth += optionWidths[i];
        
        // If we've exceeded the available width, we can't fit any more options
        if (cumulativeWidth > effectiveWidth) {
          maxOptionsToFit = i;
          break;
        }
        
        // If we've reached the end, we can fit all options
        if (i === allOptions.length - 1) {
          maxOptionsToFit = allOptions.length;
        }
      }
    }
    
    // Implement specific width-based option display as requested
    // Using ranges with ±30px flexibility
    
    // Define width ranges and corresponding number of options to show
    const getOptionsCountByWidth = (width: number): number => {
      if (width >= 1356 && width <= 1416) { // 1386 ± 30
        return 4;
      }else if (width >= 1220 && width <= 1356) { // 1386 ± 30
        return 4;
      }else if (width >= 1077 && width <= 1220) { // 952 ± 30
        return 3;

      }else if (width >= 1050 && width <= 1077) { // 952 ± 30
        return 3;
      } else if (width >= 1034 && width <= 1050) { // 952 ± 30
        return 3;
      } else if (width >= 952 && width <= 1034) { // 952 ± 30
        return 3;
      } else if (width >= 875 && width <= 952) { // 952 ± 30
        return 3;

      } else if (width >= 820 && width <= 875) { // 952 ± 30
        return 2;
        
      }
       else if (width >= 806 && width <= 820) { // 806 ± 30
        return 2;
      } else if (width >= 734 && width <= 806) { // 734 ± 30
        return 2;
      } else if (width >= 613 && width <= 734) { // 613 ± 30
        return 2;
      } else if (width >= 566 && width <= 615) { // 566 ± 30
        return 2;
      } else if (width > 982) { // Larger than 952+30
        // For very large widths, show more options
        return Math.min(5, allOptions.length);
      } else if (width > 615) { // Between ranges but larger than 566+30
        return 2;
      } else {
        // For smaller widths, show at least 1 option
        return 1;
      }
    };
    
    // Get the number of options to show based on available width
    const optionsToShow = getOptionsCountByWidth(availableWidth);
    
    // Ensure we don't try to show more options than we have
    maxOptionsToFit = Math.min(optionsToShow, allOptions.length);
    
    // Log the decision for debugging
    console.log(`Width: ${availableWidth}px, Showing ${maxOptionsToFit} options based on width ranges`);
    
    const maxOptionsToShow = Math.max(1, maxOptionsToFit);
    // console.log(`Can fit ${maxOptionsToShow} options in ${availableWidth}px width (Used: ${tempWidth || 0}px)`);
    
    // Add options until we run out of space
    for (let i = 0; i < allOptions.length; i++) {
      if (i < maxOptionsToShow) {
        first.push(allOptions[i]);
        usedWidth += optionWidths[i];
      } else {
        remaining.push(allOptions[i]);
      }
    }
    
    // console.log(`Showing ${first.length} options, hiding ${remaining.length} options`);
    console.log("Used width:", usedWidth, "Available width:", availableWidth);
    
    setFirstThreeOptions(first);
    setRemainingOptions(remaining);
  }

  // Create a ResizeObserver to monitor input field width changes
  useEffect(() => {
    if (!inputRef.current) return;
    
    // Create a ResizeObserver to monitor input field width changes
    const resizeObserver = new ResizeObserver(() => {
      // Recalculate options when input width changes
      setTimeout(() => {
        getOptionWidth();
      }, 100);
    });
    
    // Start observing the input field
    resizeObserver.observe(inputRef.current);
    
    // Also handle window resize events
    const handleResize = () => {
      setTimeout(() => {
        getOptionWidth();
      }, 150);
    };

    // Initial calculation
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      // Clean up
      if (inputRef.current) {
        resizeObserver.unobserve(inputRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [isInstructionalPanelOpen, isMaximized, allOptions.length]);
  
  // Add a separate effect to force recalculation when maximized state changes
  useEffect(() => {
    // Use a longer timeout to ensure the DOM has fully rendered after state change
    const timer = setTimeout(() => {
      if (optionsContainerRef.current && inputRef.current) {
        // console.log("Recalculating options after maximize/minimize toggle");
        getOptionWidth();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isMaximized]);

  // console.log("optionsContainerRef.current - ", optionsContainerRef.current?.clientWidth);

  return (
    <div className={`chat-footer ${isMaximized ? "maximized" : ""}`} style={{ position: "relative", zIndex: 1500 }}>
      {isMaximized && (
        <>
          <div
            className={`chat-options text-size-${textSize}`}
            style={{
              padding: "0px 10px 10px",
              fontSize: "15px",
              position: "relative",
              zIndex: 1500
            }}
            ref={optionsContainerRef}
          >
            <div
              style={{
                display: "flex",
                gap: "5px",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap", // Allow options to wrap to next line if needed
                maxWidth: "100%" // Use full width available
              }}
            >
              {firstThreeOptions.map((option, index) => (
                <div className="chat-outer" key={`outer-${index}`}>
                  <div className="chat-inner">
                    <button
                      key={`main-option-${index}`}
                      className={`chat-option text-size-${textSize}`}
                      onClick={() => handleOptionClick(option.text)}
                      style={{
                        ...(option.text === "Change address"
                          ? { color: "red", borderColor: "red" }
                          : {}),
                        fontSize: "15px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "none", // Remove max-width constraint to show full text
                        width: "auto" // Allow options to take their natural width
                      }}
                      title={option.text}
                    >
                      {option.text !== "Change address" && (
                        <div
                          className="stars-icon-svg"
                          style={{
                            marginRight: "5px",
                            flexShrink: 0,
                          }}
                        ></div>
                      )}
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getTruncatedText(option.text, index === firstThreeOptions.length - 1)}
                      </span>
                    </button>
                  </div>
                </div>
              ))}
              {remainingOptions.length > 0 && (
                <div style={{ position: "relative", zIndex: 1200 }} className="chat-outer">
                  <button
                    onClick={toggleMoreOptions}
                    style={{
                      background: "none",
                      border: "none",
                      borderRadius: "20px",
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      backgroundColor: "#f9fafb",
                      whiteSpace: "nowrap",
                      minWidth: "fit-content",
                    }}
                  >
                    <span style={{ color: "#6138F5", fontSize: "14px", whiteSpace: "nowrap" }}>
                      + {remainingOptions.length}
                    </span>
                    <img
                      src={upperArrowIcon}
                      alt="More options"
                      style={{
                        width: "12px",
                        height: "8px",
                        transform: showMoreOptions
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </button>
                  {showMoreOptions && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "45px",
                        right: "0%",
                        transform: "translateX(-0%)",
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        padding: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 1250,
                        minWidth: "340px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-6px",
                          right: "4%",
                          transform: "translateX(0%)",
                          width: "0",
                          height: "0",
                          borderLeft: "6px solid transparent",
                          borderRight: "6px solid transparent",
                          borderTop: "6px solid #ffffff",
                        }}
                      ></div>
                      {remainingOptions.map((option, index) => (
                        <button
                          key={`tooltip-option-${index}`}
                          className={`chat-option text-size-${textSize}`}
                          onClick={() => handleOptionClick(option.text)}
                          onMouseEnter={() => setHoveredOptionIndex(index)}
                          onMouseLeave={() => setHoveredOptionIndex(null)}
                          style={{
                            ...(option.text === "Change address"
                              ? { color: "red", borderColor: "red" }
                              : {}),
                            fontSize: `${textSize}%`,
                            width: "100%",
                            textAlign: "left",
                            justifyContent: "flex-start",
                            backgroundColor: hoveredOptionIndex === index ? "#f3f4f6" : "transparent",
                            transition: "background-color 0.2s ease",
                            borderRadius: "8px",
                          }}
                        >
                          {option.text !== "Change address" && (
                            <div
                              className="stars-icon-svg"
                              style={{
                                marginRight: "5px",
                              }}
                            ></div>
                          )}
                          {option.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div
        className={`chat-input-container ${
          isMaximized ? "maximized-input" : ""
        }`}
        style={{ display: showFeedback ? "none" : "flex" }}
      >
        <input
          type="text"
          placeholder="Type your question here"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (!hasTyped && e.target.value.trim() !== "") {
              setHasTyped(true);
            }
          }}
          onKeyPress={handleKeyPress}
          ref={inputRef}
          className={`${
            isMaximized ? "maximized-input-field" : ""
          } text-size-${textSize}`}
          style={{ fontSize: `${textSize}%` }}
        />
        <button
          className={`send-button ${
            isMaximized ? "maximized-send-button" : ""
          }`}
          onClick={handleSendMessage}
          disabled={!inputValue.trim()}
        >
          <div
            className={`send-icon-svg ${isMaximized ? "maximized-send-icon" : ""}`}
          ></div>
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
